import { AccessExplainerExportButton } from './AccessExplainerExportButton';
import { AccessExplainerObjectQueryParams } from './AccessExplainerFields';
import { COPY_SPECIFIC } from '@sonar-software/sonar-ts-constants';
import { createAsyncSelectors } from '@lib/selectors';
import { getDataAccessRoute } from '@lib/routes';
import { parseQueryString } from '@sonar-software/sonar-ts-lib';
import { SortByFilterQueryParams } from '@periscopes/general/filters';

import { AccessExplainerObject, AccessType, coreActions } from '@sonar-software/sonar-core';
import { convertAccessObjectToDql, ObjectTable } from './objects/ObjectTable';
import { DynamicFilter, DynamicQueryParams } from '@sonar-software/sonar-ts-types';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useAction, useQueryParams, useSonarSelector, useTableFiltering } from '@hooks';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

export const accessExplainerDataSelectors =
	createAsyncSelectors((state) => state.core.dataSecurity.dataAccess.accessExplainer.byId.get);

export type AccessExplainerQueryParams = DynamicQueryParams & SortByFilterQueryParams & {
	accessName?: string;
	type?: AccessType;
};

const convertToDql = (params: AccessExplainerQueryParams) => {
	const { accessName, sortBy, sortDirection, type, ...rest } = params;

	return convertAccessObjectToDql(sortBy, sortDirection, rest as DynamicFilter);
};

export const AccessExplainer: React.FC = () => {
	const { id } = useParams<{id: string}>();
	const lastUsedParams = useRef<AccessExplainerQueryParams>();

	const navigate = useNavigate();
	const location = useLocation();

	const getExplainer = useAction(coreActions.requestGetCoreDataSecurityAccessExplainerById);
	const data = useSonarSelector(accessExplainerDataSelectors.data);
	const isRequested = useSonarSelector(accessExplainerDataSelectors.isRequested);
	const [ isRefreshing, setIsRefreshing ] = React.useState(false);

	useEffect(() => {
		if (isRefreshing && !isRequested) {
			setIsRefreshing(false);
		}
	}, [ isRefreshing, isRequested ]);

	const parsedQuery = useMemo(
		() => parseQueryString(location.search) as AccessExplainerQueryParams,
		[ location.search ]
	);

	const initialParams = useMemo(
		() => ({
			displays: '',
			filters: '',
			ordering: '',
			...parsedQuery
		}),
		[ parsedQuery ]
	);

	const updateUrl = useCallback(
		(queryObject: AccessExplainerQueryParams) => {
			navigate(getDataAccessRoute({
				id,
				...queryObject
			}), { replace: true });
		}, [ id, navigate ]
	);

	const callApi = useCallback((params?: AccessExplainerQueryParams) => {
		if (id && params?.type) {
			lastUsedParams.current = params;

			getExplainer({
				accessId: id,
				filters: params.filters ?? '',
				type: params.type
			});
		}
	}, [ id, getExplainer ]);

	const { queryParams, updateParams: originalUpdateParams } = useQueryParams<AccessExplainerQueryParams>({
		defaultParams: initialParams,
		onUpdate: (_, __, combined) => {
			const urlParams: AccessExplainerQueryParams = {
				accessName: combined.accessName,
				displays: combined.displays ?? '',
				filters: combined.filters ?? '',
				ordering: combined.ordering ?? '',
				type: combined.type
			};

			updateUrl(urlParams);
		}
	});

	const { isFiltering, updateParams } = useTableFiltering(originalUpdateParams, isRequested);

	useEffect(() => {
		if (
			parsedQuery.accessName !== queryParams.accessName ||
			parsedQuery.type !== queryParams.type
		) {
			updateParams({
				accessName: parsedQuery.accessName,
				type: parsedQuery.type
			});
		}
	}, [ parsedQuery, queryParams, updateParams ]);

	useEffect(() => {
		if (!id || !queryParams?.type) {
			navigate(getDataAccessRoute(), { replace: true });
		}

		const lastUsed = lastUsedParams.current;

		if (queryParams.filters === lastUsed?.filters &&
			queryParams.accessName === lastUsed?.accessName &&
			queryParams.type === lastUsed?.type) {
			return;
		}

		callApi(queryParams);
	}, [ callApi, id, navigate, queryParams ]);

	const handleRowConfig = useCallback(
		(record: AccessExplainerObject) => ({
			onClick: () => {
				const queryObject: AccessExplainerObjectQueryParams = {
					accessName: queryParams.accessName,
					objectName: record.objectName,
					type: queryParams.type
				};

				navigate(
					getDataAccessRoute({
						id,
						objectName: record.objectId,
						...queryObject
					})
				);
			}
		}),
		[ id, navigate, queryParams ]
	);

	return (
		<ObjectTable
			convertToDql={ convertToDql }
			data={ data }
			defaultAccessSearchName={ queryParams.accessName }
			displayNameString={ queryParams.displays }
			exportButton={
				<AccessExplainerExportButton
					queryParams={ {
						...queryParams,
						accessId: id ?? '',
						accessType: queryParams.type!
					} } />
			}
			filterString={ queryParams.filters }
			isFiltering={ isFiltering.current }
			isLoading={ isRequested }
			isRefreshing={ isRefreshing }
			onForceRefresh={ () => {
				setIsRefreshing(true);
				callApi(queryParams);
			} }
			onRowConfig={ handleRowConfig }
			orderString={ queryParams.ordering }
			title={ COPY_SPECIFIC.ACCESS_EXPLAINER }
			updateParams={ updateParams } />);
};
